import { type ApolloError } from 'apollo-client'
import { useEffect } from 'react'
import {
  type CollectionsFormData,
  CollectionsFormMode,
  type CollectionsFormModeProps,
} from 'domains/collections/components/collections-form/utils/collectionsForm.types'
import { useGetCollectionLazyQuery } from 'domains/collections/api'
import {
  mapCollectionDataToFormData,
  collectionsFormEmptyData,
} from 'domains/collections/components/collections-form/utils/collectionsForm.utils'

interface UseCollectionsFormDataProps extends CollectionsFormModeProps {}

interface UseCollectionsFormDataResult {
  formDataSuccess: boolean
  formDataLoading: boolean
  formData?: CollectionsFormData
  formDataError?: ApolloError
}

/**
 * Returns the initial values for the form, as well as details regarding the loading state
 * for those values.
 *
 * If we are in "Update" mode, then we need to fetch the existing collection
 * data to pre-fill the form. In "Create" mode, we just skip this and return the default
 * values.
 */
export const useCollectionsFormData = ({
  formModeDetails,
}: UseCollectionsFormDataProps): UseCollectionsFormDataResult => {
  /**
   * Should be replaced by a use-lazy-query codegen hook
   */
  const [triggerFetch, apiResult] = useGetCollectionLazyQuery({ fetchPolicy: 'network-only' })

  useEffect(() => {
    if (formModeDetails.formMode === CollectionsFormMode.Update) {
      triggerFetch({
        variables: {
          id: formModeDetails.collectionId,
          retailerId: formModeDetails.retailerId,
        },
      })
    }
  }, [
    formModeDetails.collectionId,
    formModeDetails.formMode,
    formModeDetails.retailerId,
    triggerFetch,
  ])

  if (formModeDetails.formMode === CollectionsFormMode.Update) {
    // tackle when doing update
    const formData: CollectionsFormData = apiResult.data
      ? mapCollectionDataToFormData(apiResult.data.collection)
      : undefined

    return {
      formData,
      formDataSuccess: Boolean(apiResult.data),
      formDataError: apiResult.error,
      formDataLoading: apiResult.loading,
      ...apiResult,
    }
  }

  return {
    formData: collectionsFormEmptyData,
    formDataSuccess: true,
    formDataError: undefined,
    formDataLoading: false,
  }
}
